import { makeAutoObservable, runInAction } from 'mobx'

import { serverMessage } from '@/Ship/Helpers/serverError'

import { DEFAULT_STORAGES_STATE } from '../Helpers/diskAdminState'
import { LoadDiskAdminStoragesTask } from '../Tasks/LoadDiskAdminStoragesTask'

import type { DiskAdminCounts, DiskAdminStorageRow } from '../Contracts/DiskAdmin'
import type { DiskAdminStoragesState } from '../Helpers/diskAdminState'

export const STORAGES_PAGE_SIZE = 25

export { parseStoragesState, storagesStateToParams } from '../Helpers/diskAdminState'
export type { DiskAdminStoragesState } from '../Helpers/diskAdminState'

/**
 * Список дисків і просторів (`DISK-006` §3.2): фільтр-чипи з лічильниками
 * (`counts` того самого запиту), пошук, сортування за білим списком беку,
 * пагінація й вибір рядків для групової зміни квоти.
 */
export class DiskAdminStoragesStore {
    state: DiskAdminStoragesState = { ...DEFAULT_STORAGES_STATE }
    rows: DiskAdminStorageRow[] = []
    total = 0
    counts: DiskAdminCounts | null = null
    inactiveDays: number | null = null
    isLoading = false
    error: string | null = null
    /** Вибрані рядки за `key` — переживають зміну сторінки й фільтра. */
    selected = new Map<string, DiskAdminStorageRow>()

    private requestId = 0

    constructor() {
        makeAutoObservable<DiskAdminStoragesStore, 'requestId'>(this, { requestId: false })
    }

    get selectedRows(): DiskAdminStorageRow[] {
        return [...this.selected.values()]
    }

    /** Застосувати стан з адреси й перечитати, якщо він змінився. */
    apply(state: DiskAdminStoragesState): void {
        const same = JSON.stringify(state) === JSON.stringify(this.state)

        this.state = state
        if (!same || (this.counts === null && !this.isLoading)) void this.load()
    }

    async load(): Promise<void> {
        const id = ++this.requestId
        const { filter, q, sort, order, page } = this.state

        this.isLoading = true
        this.error = null

        try {
            const result = await LoadDiskAdminStoragesTask({
                filter,
                q,
                sort,
                order,
                limit: STORAGES_PAGE_SIZE,
                offset: (page - 1) * STORAGES_PAGE_SIZE,
            })

            runInAction(() => {
                if (id !== this.requestId) return
                this.rows = result.items
                this.total = result.total
                this.counts = result.counts
                this.inactiveDays = result.inactiveDays
            })
        } catch (error) {
            runInAction(() => {
                if (id === this.requestId) this.error = serverMessage(error, 'Не вдалося завантажити список дисків')
            })
        } finally {
            runInAction(() => {
                if (id === this.requestId) this.isLoading = false
            })
        }
    }

    setSelectedOnPage(keys: readonly string[]): void {
        const onPage = new Set(this.rows.map((row) => row.key))

        for (const key of [...this.selected.keys()]) {
            if (onPage.has(key) && !keys.includes(key)) this.selected.delete(key)
        }

        for (const row of this.rows) {
            if (keys.includes(row.key)) this.selected.set(row.key, row)
        }
    }

    clearSelection(): void {
        this.selected.clear()
    }
}

export const diskAdminStoragesStore = new DiskAdminStoragesStore()
